// Consolidate the tracked conditions so a disease that came from several reports
// shows once. Code-based normalization for now (a reasoning model can refine the
// grouping later). Keep this normalization in sync with
// functions/migrate-dedupe-conditions.mjs.
import type { Condition } from './types';
import { parseDate } from './labTable';

/** Stable per-condition key — matches a list row to its avatar marker (both sides
 *  dedupe the same source list, so the code/diseaseKey is identical in each). */
export function condKey(c: Condition): string {
  return c.code || diseaseKey(c.description) || (c.description || '').toLowerCase();
}

/** Normalized disease key — used to group repeats of the "same" condition. */
export function diseaseKey(desc: string): string {
  return (desc || '')
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')                                     // drop parentheticals e.g. "(T7-T12)"
    .replace(/\b(chronic|acute|early|late|mild|moderate|severe|history of|h\/o|status[- ]post|s\/p|known|possible|probable|left|right|bilateral)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const RANK: Record<string, number> = { Active: 0, Chronic: 1, Resolved: 2 };
const sinceMs = (s?: string) => (s ? parseDate(s) ?? Infinity : Infinity);

/** Merge two records of the same disease into one consolidated condition. */
function merge(a: Condition, b: Condition): Condition {
  const out: Condition = { ...a };
  if ((RANK[b.status] ?? 3) < (RANK[out.status] ?? 3)) out.status = b.status;   // most-active status wins
  if (sinceMs(b.since) < sinceMs(out.since)) out.since = b.since;               // earliest onset
  if (!out.region && b.region) out.region = b.region;
  if (!out.system && b.system) out.system = b.system;
  if (!out.pos && b.pos) out.pos = b.pos;
  if ((b.description || '').length > (out.description || '').length) out.description = b.description;   // richer label
  // Track every source doc so an edit/delete on the card can reach all of them.
  out._ids = [...(a._ids ?? (a._id ? [a._id] : [])), ...(b._ids ?? (b._id ? [b._id] : []))];
  return out;
}

/** Collapse repeats of the same disease into a single consolidated condition. */
export function dedupeConditions(list: Condition[]): Condition[] {
  const byKey = new Map<string, Condition>();
  for (const c of list) {
    const k = diseaseKey(c.description) || (c.description || '').toLowerCase();
    const cur = byKey.get(k);
    if (cur) byKey.set(k, merge(cur, c));
    else byKey.set(k, { ...c, _ids: c._id ? [c._id] : [] });
  }
  return [...byKey.values()];
}
